import React, { useState } from 'react';
import { CheckCircle, ArrowRight, DollarSign, TrendingUp, RefreshCw, Wrench, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goals = [
  { id: 'expand', label: 'Expand My Business', icon: <TrendingUp className="h-5 w-5" />, desc: 'New locations, hiring, inventory' },
  { id: 'refinance', label: 'Refinance Existing Debt', icon: <RefreshCw className="h-5 w-5" />, desc: 'Replace expensive MCA or high interest loans' },
  { id: 'restructure', label: 'Restructure Cashflow', icon: <DollarSign className="h-5 w-5" />, desc: 'Better terms, lower monthly payments' },
  { id: 'equipment', label: 'Purchase Equipment', icon: <Wrench className="h-5 w-5" />, desc: 'Machinery, vehicles, technology' },
  { id: 'working_capital', label: 'Working Capital', icon: <Briefcase className="h-5 w-5" />, desc: 'Bridge gaps, cover payroll, seize deals' },
];

const amounts = [
  'Under $50,000',
  '$50,000 to $100,000',
  '$100,000 to $200,000',
  '$200,000 to $500,000',
  '$500,000+',
  'Not sure yet',
];

const BorrowerFunnel = () => {
  const [step, setStep] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', business_name: '', estimated_amount: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/funnel/borrower`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, goal: selectedGoal }),
      });
      if (res.ok) setDone(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-4" style={{ fontFamily: 'var(--font-body)' }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-teal-500/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-blue-500/8 rounded-full blur-[120px]" />
        </div>
        <div data-testid="borrower-funnel-success" className="relative text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-teal-500/20 flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-teal-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>You're on the List</h1>
          <p className="text-slate-400 text-base leading-relaxed mb-8">
            A Quidity advisor will reach out within 24 hours to discuss your financing options. We're excited to engineer better cashflow for your business.
          </p>
          <a href="https://quidity.fund" className="text-sm text-teal-400 hover:text-teal-300 transition-colors">Visit Quidity Direct</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] relative overflow-hidden" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-teal-500/6 rounded-full blur-[180px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500/6 rounded-full blur-[150px]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-5 py-12">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="https://customer-assets.emergentagent.com/job_0073d843-75f0-4073-b1ec-cb9427d27521/artifacts/vnbckdjp_QuidityDirect%20Logo.png" alt="Quidity" className="h-8 brightness-0 invert" />
          </div>
          <p className="text-teal-400 text-xs font-semibold uppercase tracking-[0.2em]">Cashflow Engineering</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 justify-center mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 rounded-full transition-all duration-500 ${s <= step ? 'bg-teal-400 w-16' : 'bg-white/10 w-8'}`} />
          ))}
        </div>

        {/* STEP 1: Goal Selection */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              What Are You Looking to Accomplish?
            </h1>
            <p className="text-slate-400 text-center mb-10 text-base">Select the option that best describes your financing goal.</p>

            <div className="space-y-3">
              {goals.map((g) => (
                <button
                  key={g.id}
                  data-testid={`goal-${g.id}`}
                  onClick={() => { setSelectedGoal(g.id); setStep(2); }}
                  className={`w-full text-left p-5 rounded-xl border transition-all duration-300 group hover:scale-[1.01] ${
                    selectedGoal === g.id
                      ? 'border-teal-400 bg-teal-500/10'
                      : 'border-white/10 bg-white/[0.03] hover:border-teal-400/50 hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2.5 rounded-lg transition-colors ${selectedGoal === g.id ? 'bg-teal-400/20 text-teal-300' : 'bg-white/5 text-slate-400 group-hover:text-teal-400'}`}>
                      {g.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-semibold text-sm">{g.label}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{g.desc}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-teal-400 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2: Amount + Business */}
        {step === 2 && (
          <div className="animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Tell Us About Your Business
            </h1>
            <p className="text-slate-400 text-center mb-10 text-base">This helps us match you with the right solution.</p>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Business Name</label>
                <input
                  data-testid="borrower-business-name"
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-teal-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="Your business name"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Estimated Amount Needed</label>
                <div className="grid grid-cols-2 gap-2.5">
                  {amounts.map((a) => (
                    <button
                      key={a}
                      data-testid={`amount-${a.replace(/[^a-zA-Z0-9]/g, '')}`}
                      onClick={() => setForm({ ...form, estimated_amount: a })}
                      className={`p-3.5 rounded-xl text-sm font-medium border transition-all ${
                        form.estimated_amount === a
                          ? 'border-teal-400 bg-teal-500/10 text-teal-300'
                          : 'border-white/10 bg-white/[0.03] text-slate-400 hover:border-teal-400/40 hover:text-white'
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Anything Else We Should Know? (Optional)</label>
                <textarea
                  data-testid="borrower-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-teal-400/50 focus:bg-white/[0.07] transition-all resize-none"
                  placeholder="Current debt situation, timeline, etc."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(1)} className="px-6 py-4 rounded-xl border border-white/10 text-slate-400 text-sm font-medium hover:text-white hover:border-white/20 transition-all">Back</button>
              <button
                data-testid="borrower-step2-next"
                onClick={() => { if (form.business_name) setStep(3); }}
                disabled={!form.business_name}
                className="flex-1 py-4 rounded-xl bg-teal-500 text-white text-sm font-semibold hover:bg-teal-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Contact Info */}
        {step === 3 && (
          <div className="animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Where Should We Reach You?
            </h1>
            <p className="text-slate-400 text-center mb-10 text-base">A Quidity advisor will contact you within 24 hours.</p>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Full Name</label>
                <input
                  data-testid="borrower-full-name"
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-teal-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Email</label>
                <input
                  data-testid="borrower-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-teal-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="john@business.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Phone</label>
                <input
                  data-testid="borrower-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-teal-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="(555) 123 4567"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(2)} className="px-6 py-4 rounded-xl border border-white/10 text-slate-400 text-sm font-medium hover:text-white hover:border-white/20 transition-all">Back</button>
              <button
                data-testid="borrower-submit"
                onClick={handleSubmit}
                disabled={!form.full_name || !form.email || !form.phone || submitting}
                className="flex-1 py-4 rounded-xl bg-teal-500 text-white text-sm font-semibold hover:bg-teal-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? 'Submitting...' : 'Get My Options'} {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>

            <p className="text-center text-xs text-slate-600 mt-6">We respect your privacy. No spam, ever.</p>
          </div>
        )}

        {/* Trust bar */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { value: '$2B+', label: 'Capital Deployed' },
              { value: '1,000+', label: 'Businesses Funded' },
              { value: '24hrs', label: 'Typical Approval' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorrowerFunnel;
